import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ExternalLink,
  FileText,
  Link2,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { invoiceApi } from "../../backend/api/invoices";
import type {
  Invoice,
  InvoiceLine,
  LineAmountTypes,
  UnbilledCost,
} from "../../backend/api/invoices";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const CUSTOM = "Custom";

const money = (value?: string | number | null) => {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-AU", { style: "currency", currency: "AUD" })
    : "$0.00";
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString("en-AU");
};

/** Colour carries the exception; anything in progress stays plain. */
const statusColour = (invoice: Invoice) => {
  // Unbilled work outranks whatever Xero says the invoice is doing: it is the
  // thing that needs attention.
  if (
    Number(invoice.stranded_costs || 0) > 0 ||
    Number(invoice.unrecorded_lines || 0) > 0
  ) {
    return "text-red-600";
  }
  if (invoice.status === "draft") return "text-muted-foreground";
  if (invoice.status === "voided") return "text-amber-600";
  if (statusLabel(invoice) === "Overdue") return "text-red-600";
  if (Number(invoice.amount_due ?? 0) === 0) return "text-emerald-600";
  return "text-foreground";
};

const titleCase = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

/**
 * Xero's API and Xero's screens use different words for the same thing: the API
 * says AUTHORISED where the screen says Awaiting Payment. The portal follows the
 * screen, so an invoice reads the same here as it does in Xero.
 */
const xeroStatusLabel = (invoice: Invoice) => {
  const due = Number(invoice.amount_due ?? 0);
  const paid = Number(invoice.amount_paid ?? 0);

  switch ((invoice.xero_status || "").toUpperCase()) {
    case "DRAFT":
      return "Draft";
    case "SUBMITTED":
      return "Awaiting Approval";
    case "AUTHORISED": {
      if (due <= 0) return "Paid";
      // Xero shows an unpaid invoice past its due date as overdue, which is the
      // distinction anyone chasing payment actually cares about.
      const overdue =
        invoice.due_date && new Date(invoice.due_date) < new Date();
      if (overdue) return "Overdue";
      return paid > 0 ? "Part Paid" : "Awaiting Payment";
    }
    case "PAID":
      return "Paid";
    case "VOIDED":
      return "Voided";
    case "DELETED":
      return "Deleted";
    default:
      return "Issued";
  }
};

const statusLabel = (invoice: Invoice) => {
  const base =
    invoice.status === "issued" ? xeroStatusLabel(invoice) : titleCase(invoice.status);

  const hasIssues =
    Number(invoice.stranded_costs || 0) > 0 ||
    Number(invoice.unrecorded_lines || 0) > 0;
  return hasIssues ? `${base} - Issues Identified` : base;
};

type EditableLine = InvoiceLine & { key: string };

const lineFromCost = (cost: UnbilledCost, index: number): EditableLine => ({
  key: `cost-${cost.payment_id}`,
  description: cost.item || `Cost ${cost.payment_id}`,
  // Billed as a single line at the net amount, so the discount the portal
  // applied is the amount that reaches Xero rather than the gross.
  quantity: "1",
  unit_amount: String(cost.net_cost ?? cost.cost ?? "0"),
  gst_applies: cost.gst_applies ?? true,
  payment_id: cost.payment_id,
  position: index,
});

export default function ProjectInvoices({
  projectId,
  clientName,
  onChanged,
}: {
  projectId: string | number;
  clientName: string;
  onChanged?: () => void;
}) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [unbilled, setUnbilled] = useState<UnbilledCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [composing, setComposing] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [reviewing, setReviewing] = useState<Invoice | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: loadError } = await invoiceApi.forProject(projectId);
    setLoading(false);

    if (loadError) {
      setError(loadError.message || "Could not load invoices.");
      return;
    }

    setError("");
    setInvoices(data?.invoices || []);
    setUnbilled(data?.unbilled || []);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const issue = async (invoice: Invoice) => {
    setBusy(`issue-${invoice.id}`);
    setError("");
    const { error: issueError } = await invoiceApi.issue(invoice.id);
    setBusy("");

    if (issueError) {
      setError(issueError.message || "Xero would not accept this invoice.");
    }
    await load();
    onChanged?.();
  };

  const refresh = async (invoice: Invoice) => {
    setBusy(`refresh-${invoice.id}`);
    const { error: refreshError } = await invoiceApi.refresh(invoice.id);
    setBusy("");
    if (refreshError) {
      setError(refreshError.message || "Could not read this invoice from Xero.");
    }
    await load();
    onChanged?.();
  };

  const remove = async (invoice: Invoice) => {
    if (
      !window.confirm(
        "Delete this draft? The costs it covers become billable again.",
      )
    ) {
      return;
    }

    setBusy(`delete-${invoice.id}`);
    const { error: deleteError } = await invoiceApi.remove(invoice.id);
    setBusy("");
    if (deleteError) {
      setError(deleteError.message || "Could not delete this draft.");
    }
    await load();
    onChanged?.();
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Invoices
        </CardTitle>
        <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => setAttaching(true)}
          title="Link an invoice that was raised in Xero directly"
        >
          <Link2 className="h-4 w-4" />
          Attach from Xero
        </Button>
        <Button
          onClick={() => setComposing(true)}
          disabled={loading || unbilled.length === 0}
          title={
            unbilled.length === 0
              ? "Every cost has been invoiced. Add a cost to raise another."
              : `Invoice the ${unbilled.length} cost${unbilled.length === 1 ? "" : "s"} not yet billed`
          }
        >
          <Plus className="h-4 w-4" />
          Generate invoice
        </Button>
        </div>
      </CardHeader>

      <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
        {error && (
          <p className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {unbilled.length > 0 && (
          <p className="mb-3 text-sm text-muted-foreground">
            {unbilled.length} {unbilled.length === 1 ? "cost" : "costs"} not yet
            invoiced.
          </p>
        )}

        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Raised</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-20 text-center text-muted-foreground">
                    Loading invoices...
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-20 text-center text-muted-foreground">
                    No invoices yet.
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">
                      {invoice.xero_invoice_number || `Draft ${invoice.id}`}
                    </TableCell>
                    <TableCell>{formatDate(invoice.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {money(invoice.total ?? invoice.subtotal)}
                      {invoice.status === "draft" && (
                        <span className="block text-xs text-muted-foreground">
                          before GST
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {invoice.status === "issued" ? money(invoice.amount_due) : "-"}
                    </TableCell>
                    <TableCell className={`whitespace-nowrap ${statusColour(invoice)}`}>
                      {statusLabel(invoice)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {invoice.status === "draft" && (
                          <>
                            <Button
                              size="sm"
                              disabled={busy !== ""}
                              onClick={() => issue(invoice)}
                            >
                              <Send className="h-4 w-4" />
                              {busy === `issue-${invoice.id}`
                                ? "Sending..."
                                : "Create in Xero"}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Delete draft"
                              disabled={busy !== ""}
                              onClick={() => remove(invoice)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}

                        {(Number(invoice.stranded_costs || 0) > 0 ||
                          Number(invoice.unrecorded_lines || 0) > 0) && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setReviewing(invoice)}
                            title="This invoice no longer bills every cost it holds"
                          >
                            <AlertTriangle className="h-4 w-4" />
                            Review
                          </Button>
                        )}

                        {invoice.status === "issued" && (
                          <>
                            <Button variant="outline" size="sm" asChild>
                              {/* Xero's own rendered PDF, not one we build. */}
                              <a
                                href={invoiceApi.pdfUrl(invoice.id)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <ExternalLink className="h-4 w-4" />
                                PDF
                              </a>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Refresh from Xero"
                              title="Read payment status from Xero"
                              disabled={busy !== ""}
                              onClick={() => refresh(invoice)}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <ReviewInvoice
        invoice={reviewing}
        onClose={() => setReviewing(null)}
        onResolved={async () => {
          setReviewing(null);
          await load();
          onChanged?.();
        }}
        onError={setError}
      />

      <AttachInvoice
        open={attaching}
        projectId={projectId}
        onClose={() => setAttaching(false)}
        onAttached={async () => {
          setAttaching(false);
          await load();
          onChanged?.();
        }}
      />

      <ComposeInvoice
        open={composing}
        projectId={projectId}
        clientName={clientName}
        unbilled={unbilled}
        onClose={() => setComposing(false)}
        onCreated={async () => {
          setComposing(false);
          await load();
          onChanged?.();
        }}
        onError={setError}
      />
    </Card>
  );
}

/**
 * Builds a draft from the costs not yet billed.
 *
 * The GST control is two levels because Xero is: the header dropdown sets
 * whether amounts include GST for the whole invoice, and each line decides
 * whether GST applies at all. The header shows Custom when the lines disagree,
 * which is the honest description of that state rather than a third setting.
 */
function ComposeInvoice({
  open,
  projectId,
  clientName,
  unbilled,
  onClose,
  onCreated,
  onError,
}: {
  open: boolean;
  projectId: string | number;
  clientName: string;
  unbilled: UnbilledCost[];
  onClose: () => void;
  onCreated: () => void;
  onError: (message: string) => void;
}) {
  const [contact, setContact] = useState(clientName);
  const [amountTypes, setAmountTypes] = useState<LineAmountTypes>("Exclusive");
  const [reference, setReference] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setContact(clientName);
    setLines(unbilled.map(lineFromCost));
    setReference("");
    setDueDate("");
    setAmountTypes("Exclusive");
  }, [open, clientName, unbilled]);

  // Custom is a description of the lines, not a setting of its own.
  const gstSummary = useMemo(() => {
    if (lines.length === 0) return "All lines";
    const first = lines[0].gst_applies;
    return lines.every((line) => line.gst_applies === first)
      ? first
        ? "All lines"
        : "No lines"
      : CUSTOM;
  }, [lines]);

  const subtotal = useMemo(
    () =>
      lines.reduce(
        (sum, line) => sum + Number(line.quantity || 0) * Number(line.unit_amount || 0),
        0,
      ),
    [lines],
  );

  const setAllGst = (applies: boolean) =>
    setLines((current) => current.map((line) => ({ ...line, gst_applies: applies })));


  const save = async () => {
    if (!contact.trim()) {
      onError("An invoice needs a client to bill.");
      return;
    }
    if (lines.length === 0) {
      onError("An invoice needs at least one line.");
      return;
    }

    setSaving(true);
    const { error } = await invoiceApi.create(projectId, {
      contact_name: contact.trim(),
      line_amount_types: amountTypes,
      reference: reference.trim() || null,
      due_date: dueDate || null,
      lines: lines.map((line) => ({
        description: line.description,
        quantity: line.quantity || "1",
        unit_amount: line.unit_amount || "0",
        gst_applies: line.gst_applies,
        payment_id: line.payment_id,
      })),
    });
    setSaving(false);

    if (error) {
      onError(error.message || "Could not create this invoice.");
      return;
    }

    onCreated();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Generate invoice</DialogTitle>
          <DialogDescription>
            Every cost on this project that has not been invoiced yet. Creating
            it marks those costs as invoiced, so they are never billed twice.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="invoice-contact">Bill to</Label>
            <Input
              id="invoice-contact"
              value={contact}
              onChange={(event) => setContact(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="invoice-due">Due date</Label>
            <Input
              id="invoice-due"
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="invoice-amounts">Amounts</Label>
            <Select
              value={amountTypes}
              onValueChange={(value) => setAmountTypes(value as LineAmountTypes)}
            >
              <SelectTrigger id="invoice-amounts">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Exclusive">GST exclusive</SelectItem>
                <SelectItem value="Inclusive">GST inclusive</SelectItem>
                <SelectItem value="NoTax">No GST</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="invoice-gst">GST applies to</Label>
            <Select
              value={gstSummary}
              onValueChange={(value) => {
                if (value === CUSTOM) return;
                setAllGst(value === "All lines");
              }}
            >
              <SelectTrigger id="invoice-gst">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All lines">All lines</SelectItem>
                <SelectItem value="No lines">No lines</SelectItem>
                {gstSummary === CUSTOM && (
                  <SelectItem value={CUSTOM}>Custom</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="invoice-reference">Reference</Label>
          <Input
            id="invoice-reference"
            placeholder="Shown on the invoice"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
        </div>

        <div className="max-h-[40vh] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cost</TableHead>
                <TableHead className="w-32 text-right">Amount</TableHead>
                <TableHead className="w-28">GST</TableHead>
                <TableHead className="w-[1%]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-20 text-center text-muted-foreground">
                    Nothing left to invoice. Add a cost to the project first.
                  </TableCell>
                </TableRow>
              ) : (
                lines.map((line, index) => (
                  <TableRow key={line.key}>
                    <TableCell className="font-medium">{line.description}</TableCell>
                    <TableCell className="text-right">
                      {money(line.unit_amount)}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={line.gst_applies ? "yes" : "no"}
                        onValueChange={(value) =>
                          setLines((current) =>
                            current.map((entry, at) =>
                              at === index
                                ? { ...entry, gst_applies: value === "yes" }
                                : entry,
                            ),
                          )
                        }
                      >
                        <SelectTrigger aria-label={`GST for line ${index + 1}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">GST</SelectItem>
                          <SelectItem value="no">Free</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove line ${index + 1}`}
                        onClick={() =>
                          setLines((current) => current.filter((_, at) => at !== index))
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm">
            Subtotal <span className="font-medium">{money(subtotal)}</span>
            <span className="text-muted-foreground">
              {amountTypes === "Inclusive" ? " (GST included)" : ""}
            </span>
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || lines.length === 0}
          >
            {saving ? "Creating..." : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Links an invoice that was raised in Xero directly.
 *
 * Deliberately one invoice at a time, by a number someone types. The portal
 * never lists invoices from Xero, so an organisation's older invoices stay
 * invisible; only the ones explicitly attached here become visible, and from
 * then on they behave exactly like invoices raised in the portal.
 */
function AttachInvoice({
  open,
  projectId,
  onClose,
  onAttached,
}: {
  open: boolean;
  projectId: string | number;
  onClose: () => void;
  onAttached: () => void;
}) {
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");

  useEffect(() => {
    if (open) {
      setNumber("");
      setProblem("");
    }
  }, [open]);

  const attach = async () => {
    const wanted = number.trim();
    if (!wanted) {
      setProblem("Enter the invoice number as it reads in Xero.");
      return;
    }

    setBusy(true);
    setProblem("");
    const { error } = await invoiceApi.attach(projectId, wanted);
    setBusy(false);

    if (error) {
      // Stays in the dialog: the number that failed is still on screen to
      // correct, and nobody has to close anything to find out why.
      setProblem(
        error.message ||
          `No invoice numbered ${wanted} was found in the connected Xero organisation. Check it matches Xero exactly, including any leading zeros.`,
      );
      return;
    }

    onAttached();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach an invoice from Xero</DialogTitle>
          <DialogDescription>
            For an invoice raised in Xero rather than here. Enter its number and
            it will be linked to this project, so its payment status appears
            alongside the invoices the portal raised. Nothing else is imported.
          </DialogDescription>
        </DialogHeader>

        {problem && (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {problem}
          </p>
        )}

        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Attaching does not touch this project's costs. If any of them were
          already billed on this Xero invoice, they will still show as not yet
          invoiced and would be billed a second time by Generate invoice. Check
          the costs list afterwards and remove or mark anything already covered.
        </p>

        <div className="grid gap-2">
          <Label htmlFor="attach-number">Invoice number</Label>
          <Input
            id="attach-number"
            placeholder="INV-0042"
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") attach();
            }}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={attach} disabled={busy || !number.trim()}>
            {busy ? "Looking up..." : "Attach invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Side by side: what this invoice bills in Xero, against the costs it holds but
 * does not bill.
 *
 * Both ways out are offered because both are legitimate. If the work belongs on
 * this invoice, add it back. If it has been billed elsewhere or is no longer
 * wanted here, release it so it can go on another. Choosing wrongly either
 * bills the client twice or loses the revenue, which is why the portal presents
 * the comparison instead of picking.
 */
function ReviewInvoice({
  invoice,
  onClose,
  onResolved,
  onError,
}: {
  invoice: Invoice | null;
  onClose: () => void;
  onResolved: () => void;
  onError: (message: string) => void;
}) {
  const [billed, setBilled] = useState<InvoiceLine[]>([]);
  const [stranded, setStranded] = useState<any[]>([]);
  const [unrecorded, setUnrecorded] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!invoice) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      const { data, error } = await invoiceApi.review(invoice.id);
      if (cancelled) return;
      setLoading(false);

      if (error) {
        onError(error.message || "Could not load this review.");
        return;
      }
      setBilled(data?.billed || []);
      setStranded(data?.stranded || []);
      setUnrecorded(data?.unrecorded || []);
    })();

    return () => {
      cancelled = true;
    };
    // onError is stable enough for this; re-running on it would refetch endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const restore = async () => {
    if (!invoice) return;
    setBusy("restore");
    const { error } = await invoiceApi.restoreCosts(invoice.id);
    setBusy("");
    if (error) {
      onError(error.message || "Xero would not take the change.");
      return;
    }
    onResolved();
  };

  const adopt = async () => {
    if (!invoice) return;
    setBusy("adopt");
    const { error } = await invoiceApi.adoptLines(invoice.id);
    setBusy("");
    if (error) {
      onError(error.message || "Could not record those lines as costs.");
      return;
    }
    onResolved();
  };

  const release = async () => {
    if (!invoice) return;
    setBusy("release");
    const { error } = await invoiceApi.releaseCosts(invoice.id);
    setBusy("");
    if (error) {
      onError(error.message || "Could not release those costs.");
      return;
    }
    onResolved();
  };

  const strandedTotal = stranded.reduce(
    (sum, cost) => sum + Number(cost.net_cost || 0),
    0,
  );

  return (
    <Dialog open={Boolean(invoice)} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            Review {invoice?.xero_invoice_number || "invoice"}
          </DialogTitle>
          <DialogDescription>
            This invoice was changed in Xero and no longer bills every cost it is
            holding. Check Xero before choosing: if that work is already covered
            there under different wording, releasing would bill the client twice.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Loading...
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-md border">
              <div className="border-b px-3 py-2 text-sm font-medium">
                Billed in Xero now
                {unrecorded.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-red-600">
                    {unrecorded.length} not on the project
                  </span>
                )}
              </div>
              <ul className="divide-y text-sm">
                {billed.length === 0 ? (
                  <li className="px-3 py-3 text-muted-foreground">No lines.</li>
                ) : (
                  billed.map((line, index) => (
                    <li
                      key={line.id ?? index}
                      className="flex justify-between gap-3 px-3 py-2"
                    >
                      <span className="min-w-0 break-words">
                        {line.description}
                        {!line.payment_id && (
                          <span className="block text-xs text-red-600">
                            not recorded as a project cost
                          </span>
                        )}
                      </span>
                      <span className="whitespace-nowrap">
                        {money(
                          Number(line.quantity || 1) * Number(line.unit_amount || 0),
                        )}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            </div>

            <div className="rounded-md border border-red-200">
              <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
                Held but not billed
              </div>
              <ul className="divide-y text-sm">
                {stranded.length === 0 ? (
                  <li className="px-3 py-3 text-muted-foreground">
                    Nothing outstanding.
                  </li>
                ) : (
                  stranded.map((cost) => (
                    <li
                      key={cost.payment_id}
                      className="flex justify-between gap-3 px-3 py-2"
                    >
                      <span className="min-w-0 break-words">{cost.item}</span>
                      <span className="whitespace-nowrap">
                        {money(cost.net_cost)}
                      </span>
                    </li>
                  ))
                )}
              </ul>
              {stranded.length > 0 && (
                <div className="flex justify-between border-t px-3 py-2 text-sm font-medium">
                  <span>Total at risk</span>
                  <span>{money(strandedTotal)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          {/* No third option: doing nothing leaves the work unbilled and
              invisible, so the dialog offers only the two real resolutions.
              Escape and the close button still dismiss without deciding. */}
          <Button
            variant="outline"
            onClick={release}
            disabled={busy !== "" || stranded.length === 0}
            title="Free these costs so they can go on a different invoice"
          >
            {busy === "release" ? "Releasing..." : "Release costs"}
          </Button>
          <Button
            variant="outline"
            onClick={adopt}
            disabled={busy !== "" || unrecorded.length === 0}
            title="Record what Xero bills as costs on this project"
          >
            {busy === "adopt" ? "Recording..." : "Record lines as costs"}
          </Button>
          <Button
            onClick={restore}
            disabled={busy !== "" || stranded.length === 0}
            title="Add these costs back onto this invoice in Xero"
          >
            {busy === "restore" ? "Adding..." : "Add back to this invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
